import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGlobalState } from './useGlobalState'
import { getLifeSpaceIcon } from '../lib/icons/iconRegistry'
import { applySpaceTheme } from '../lib/theme/spaceColors'
// import { AllSpacesIcon } from '../components/icons/AllSpacesIcon' // kommt mit „Alle Lebensbereiche" zurück (SPÄTER)
import { PersonalSpaceIcon } from '../components/icons/PersonalSpaceIcon'
import { WorkSpaceIcon } from '../components/icons/WorkSpaceIcon'
import { HomeSpaceIcon } from '../components/icons/HomeSpaceIcon'

/**
 * Represents a single Life Space (default or custom).
 *
 * @property id - Unique identifier (`'all'`, `'personal'`, `'work'`, `'home'`, or `'custom-<timestamp>'`)
 * @property name - Display name shown in tooltips and dialogs
 * @property icon - React component rendered as the space icon
 * @property iconName - Lucide icon name for serialization (e.g. `'Star'`, `'Rocket'`)
 * @property color - Hex color used for icon tint and active state (e.g. `'#6d28d9'`)
 * @property badge - Optional notification count displayed on the space button
 * @property isCustom - Whether this is a user-created space (default spaces cannot be deleted)
 * @property order - Sort position in the sidebar (0-based)
 */
export interface LifeSpace {
  id: string
  name: string
  icon: React.ComponentType<{ size?: number; color?: string }>
  iconName: string
  color?: string
  badge?: number
  isCustom?: boolean
  order: number
}

/**
 * Data required to create or update a custom Life Space.
 *
 * @property name - Display name (max 20 characters)
 * @property iconName - Lucide icon name from the registry (e.g. `'Rocket'`)
 * @property color - Hex color string (e.g. `'#FF6B6B'`)
 */
export interface CreateLifeSpaceData {
  name: string
  iconName: string
  color: string
}

/** Max custom spaces a user can create */
const MAX_CUSTOM_SPACES = 10

/*
  **Drei feste Lebensbereiche** (MVP-Schnitt 2026-09-08).

  „Alle Lebensbereiche" (`all`) ist versteckt, nicht gelöscht — es steht unter
  SPÄTER und kommt wieder. Es war ohnehin **kein Lebensbereich, sondern eine
  Sicht**: Kein gespeicherter Datensatz trägt `'all'` (`type LifeSpace` in
  `db/models.ts` kennt nur die drei), und die Route lebt weiter.

  Folge, die man sehen wird: Die Tastenkürzel rücken auf — Persönlich ist jetzt
  die 1.
*/
// Default Life Spaces (cannot be deleted). Names are i18n keys resolved in the hook via useMemo.
const DEFAULT_SPACE_IDS = ['personal', 'work', 'home'] as const

const defaultLifeSpaces: LifeSpace[] = [
  // { id: 'all', name: 'All', icon: AllSpacesIcon, iconName: 'AllSpaces', color: '#2e1065', isCustom: false, order: 0 },
  { id: 'personal', name: 'Personal', icon: PersonalSpaceIcon, iconName: 'PersonalSpace', color: '#6d28d9', isCustom: false, order: 1 },
  { id: 'work', name: 'Work', icon: WorkSpaceIcon, iconName: 'WorkSpace', color: '#4338ca', isCustom: false, order: 2 },
  { id: 'home', name: 'Home', icon: HomeSpaceIcon, iconName: 'HomeSpace', color: '#0f766e', isCustom: false, order: 3 }
]

interface PersistedCustomSpace {
  id: string
  name: string
  iconName: string
  color?: string
  badge?: number
  isCustom?: boolean
  order: number
}

const persistSpaces = (spaces: LifeSpace[]): boolean => {
  try {
    const customSpaces: PersistedCustomSpace[] = spaces
      .filter(s => s.isCustom)
      .map(({ icon, ...rest }) => rest)

    localStorage.setItem('customLifeSpaces', JSON.stringify(customSpaces))

    const spaceOrder = spaces.map(s => ({ id: s.id, order: s.order }))
    localStorage.setItem('spaceOrder', JSON.stringify(spaceOrder))
    return true
  } catch (e) {
    console.error('Failed to persist spaces:', e)
    return false
  }
}

/**
 * **Eigene Lebensbereiche werden geräumt** (MVP-Schnitt, entschieden
 * 2026-09-08): Im MVP ist ein Lebensbereich einer von drei festen, und einen
 * Knopf zum Anlegen gibt es nicht mehr. Ein eigener Bereich, den niemand mehr
 * erzeugen kann, stünde sonst weiter in der Leiste.
 *
 * **Es geht dabei nichts verloren:** Eigene Bereiche leben ausschließlich hier
 * im `localStorage`. Der Typ `LifeSpace` in `db/models.ts` kennt nur
 * `personal | work | home` — kein Datensatz kann zu einem eigenen Bereich
 * gehören, es gibt also keine verwaisten Aufgaben.
 *
 * **Beim Laden, nicht als einmalige Migration:** Andere Fenster schicken ihren
 * Stand per IPC zurück, und ein einmaliger Lauf ließe die Schlüssel dadurch
 * wieder auferstehen.
 */
const purgeCustomSpaces = (): void => {
  try {
    localStorage.removeItem('customLifeSpaces')
    localStorage.removeItem('spaceOrder')
  } catch {
    // SecurityError in eingeschränkten Kontexten — dann bleibt es bei der
    // Anzeige ohne eigene Bereiche, und das ist der sichtbare Teil.
  }
}

const loadLifeSpaces = (): LifeSpace[] => {
  purgeCustomSpaces()
  return defaultLifeSpaces
}

// Serialize spaces for IPC (strip non-cloneable icon components)
const serializeSpacesForIpc = (spaces: LifeSpace[]) => {
  return spaces.map(({ icon, ...rest }) => rest)
}

/**
 * Hook for managing Life Spaces state, persistence, and cross-window sync.
 *
 * @description
 * Provides CRUD operations for Life Spaces (4 default + up to 10 custom).
 * Persists custom spaces and order to localStorage, and broadcasts changes
 * to other Electron windows via IPC.
 *
 * @returns An object containing:
 * - `spaces` — All Life Spaces (default + custom), sorted by order
 * - `activeLifeSpace` — ID of the currently active space
 * - `setActiveLifeSpace` — Switch to a different space by ID
 * - `addSpace` — Create a new custom space (returns `null` if max reached)
 * - `updateSpace` — Update name, icon, or color of a custom space
 * - `deleteSpace` — Remove a custom space by ID
 * - `reorderSpaces` — Move a space from one index to another
 *
 * @example
 * const { spaces, activeLifeSpace, setActiveLifeSpace } = useLifeSpaces()
 *
 * @example
 * // Create a custom space
 * const newSpace = addSpace({ name: 'Fitness', iconName: 'Trophy', color: '#FF6B6B' })
 */
export const useLifeSpaces = () => {
  const { updateState } = useGlobalState()
  const { t } = useTranslation()
  const [spaces, setSpaces] = useState<LifeSpace[]>([])
  const [activeSpace, setActiveSpaceLocal] = useState('personal')

  // Translate default space names reactively when language changes
  const translatedSpaces = useMemo(() => {
    return spaces.map(space =>
      (DEFAULT_SPACE_IDS as readonly string[]).includes(space.id)
        ? { ...space, name: t(`lifeSpaces.${space.id}`) }
        : space
    )
  }, [spaces, t])

  useEffect(() => {
    const loadedSpaces = loadLifeSpaces()
    setSpaces(loadedSpaces)

    const savedActiveSpace = localStorage.getItem('activeLifeSpace') || 'personal'
    // Validate that saved space still exists
    const validSpace = loadedSpaces.some(s => s.id === savedActiveSpace)
      ? savedActiveSpace
      : 'personal'

    setActiveSpaceLocal(validSpace)
    updateState('activeLifeSpace', validSpace)
    applySpaceTheme(validSpace)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Creates a new custom Life Space and persists it to localStorage.
   *
   * @param data - Name, icon name, and color for the new space
   * @returns The newly created {@link LifeSpace}, or `null` if the max (10 custom) is reached
   */
  const addSpace = useCallback((data: CreateLifeSpaceData): LifeSpace | null => {
    if (spaces.filter(s => s.isCustom).length >= MAX_CUSTOM_SPACES) {
      console.warn('Max custom life spaces reached')
      return null
    }

    const newSpace: LifeSpace = {
      id: `custom-${Date.now()}`,
      name: data.name,
      icon: getLifeSpaceIcon(data.iconName),
      iconName: data.iconName || 'Star',
      color: data.color,
      isCustom: true,
      order: spaces.length,
      badge: 0
    }

    const updatedSpaces = [...spaces, newSpace]
    setSpaces(updatedSpaces)
    persistSpaces(updatedSpaces)
    window.api?.send?.('life-spaces:updated', serializeSpacesForIpc(updatedSpaces))

    return newSpace
  }, [spaces])

  /**
   * Updates an existing custom Life Space. Default spaces are ignored.
   *
   * @param id - ID of the space to update
   * @param updates - Partial fields to merge (name, iconName, color)
   */
  const updateSpace = useCallback((id: string, updates: Partial<CreateLifeSpaceData>) => {
    const updatedSpaces = spaces.map(space => {
      if (space.id === id && space.isCustom) {
        const iconName = updates.iconName || space.iconName
        return {
          ...space,
          name: updates.name || space.name,
          icon: getLifeSpaceIcon(iconName),
          iconName,
          color: updates.color || space.color
        }
      }
      return space
    })

    setSpaces(updatedSpaces)
    persistSpaces(updatedSpaces)
    window.api?.send?.('life-spaces:updated', serializeSpacesForIpc(updatedSpaces))
  }, [spaces])

  /**
   * Removes a custom Life Space and recalculates order indices.
   * Default spaces cannot be deleted and will be silently ignored.
   *
   * @param id - ID of the space to delete
   */
  const deleteSpace = useCallback((id: string) => {
    const filtered = spaces.filter(s => s.id !== id || !s.isCustom)
    const updatedSpaces = filtered.map((space, index) => ({ ...space, order: index }))

    setSpaces(updatedSpaces)
    persistSpaces(updatedSpaces)
    window.api?.send?.('life-spaces:updated', serializeSpacesForIpc(updatedSpaces))
  }, [spaces])

  /**
   * Moves a space from one position to another and recalculates order indices.
   * Triggered by drag-and-drop in the sidebar.
   *
   * @param fromIndex - Current index in the spaces array
   * @param toIndex - Target index to move the space to
   */
  const reorderSpaces = useCallback((fromIndex: number, toIndex: number) => {
    const result = Array.from(spaces)
    const [removed] = result.splice(fromIndex, 1)
    result.splice(toIndex, 0, removed)

    const updatedSpaces = result.map((space, index) => ({ ...space, order: index }))

    setSpaces(updatedSpaces)
    persistSpaces(updatedSpaces)
    window.api?.send?.('life-spaces:updated', serializeSpacesForIpc(updatedSpaces))
  }, [spaces])

  /**
   * Switches the active Life Space. Persists to localStorage, updates
   * global state, and notifies other Electron windows via IPC.
   *
   * @param spaceId - ID of the space to activate
   */
  const setActiveLifeSpace = useCallback((spaceId: string) => {
    setActiveSpaceLocal(spaceId)
    localStorage.setItem('activeLifeSpace', spaceId)
    window.api?.send?.('active-life-space:changed', spaceId)
    updateState('activeLifeSpace', spaceId)
    applySpaceTheme(spaceId)
  }, [updateState])

  // Listen for updates from other windows
  useEffect(() => {
    if (!window.api?.receive) return

    const unsubSpaces = window.api.receive('life-spaces:updated', (updatedSpaces: LifeSpace[]) => {
      if (Array.isArray(updatedSpaces)) {
        setSpaces(updatedSpaces)
      }
    })

    const unsubActive = window.api.receive('active-life-space:changed', (spaceId: string) => {
      setActiveSpaceLocal(spaceId)
      applySpaceTheme(spaceId)
    })

    return () => {
      unsubSpaces?.()
      unsubActive?.()
    }
  }, [])

  return {
    spaces: translatedSpaces,
    activeLifeSpace: activeSpace,
    setActiveLifeSpace,
    addSpace,
    updateSpace,
    deleteSpace,
    reorderSpaces
  }
}
